/** The first page: is the service healthy, and is anything going unbilled. */

import { useEffect, useState } from 'react'
import {
  Caption1,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  EmptyState,
  KpiCard,
  LoadingState,
  MetricPill,
  SectionCard,
  SectionLabel,
  StatusBadge,
  formatTimestamp,
} from '../platform'
import { incompleteCaption, incompleteTotal } from './incompleteReading'
import { unownedCaption, unownedShown, unownedTotal } from './unownedReading'
import { orphanCaption } from './orphanReading'
import { rangeRefusal, refusalReading } from './refusalRate'
import OverviewGraphs from './OverviewGraphs'
import RangePicker from './RangePicker'
import {
  DEFAULT_RANGE,
  rangeLabel,
  toQuery as toRangeQuery,
  whyRefused,
  zoomedTo,
  type OverviewRange,
} from './overviewRange'
import { api } from '../api'
import { laneAppearance } from './laneAppearance'
import OverviewDashboard from './OverviewDashboard'
import { observationAge, useObservationClock } from './observationAge'
import type { AuthorityHealth, Overview as OverviewData, OverviewSeries } from '../api'

/** The OCPP Server's end to end window. Everything else has to fit inside it. */
const OCPP_WINDOW_MS = 3000

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  grid: {
    display: 'grid',
    // Six cards fit one row on a laptop. At 220 px the sixth wrapped onto a row
    // of its own, which read as a card that belonged to nothing.
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  authorities: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  shadow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  },
  estateNote: {
    display: 'block',
    marginTop: '12px',
    color: tokens.colorNeutralForeground3,
  },
  note: {
    display: 'block',
    marginTop: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
})



export default function Overview() {
  const styles = useStyles()
  const [data, setData] = useState<OverviewData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [range, setRange] = useState<OverviewRange>(DEFAULT_RANGE)
  // Where the reader was before each zoom, so a drag that went too far is one
  // press of Back rather than a rebuild of the range from the date pickers.
  const [earlier, setEarlier] = useState<OverviewRange[]>([])
  const [series, setSeries] = useState<OverviewSeries | null>(null)
  const [seriesError, setSeriesError] = useState<string | null>(null)
  const [refetching, setRefetching] = useState(false)

  useEffect(() => {
    const load = () =>
      api.get<OverviewData>('/overview').then(setData).catch((e) => setError(e.message))
    void load()
    const timer = setInterval(load, 10000)
    return () => clearInterval(timer)
  }, [])

  // The bound comes from the last answer, so the console refuses an impossible
  // range without keeping its own copy of the number.
  const maxDays = series?.max_range_days ?? null
  const refused = whyRefused(range, maxDays)

  useEffect(() => {
    if (refused) return
    let current = true
    setRefetching(true)
    api
      .get<OverviewSeries>(`/overview/series${toRangeQuery(range)}`)
      .then((answer) => {
        if (!current) return
        setSeries(answer)
        setSeriesError(null)
      })
      .catch((e) => current && setSeriesError(e.message))
      .finally(() => current && setRefetching(false))
    return () => {
      current = false
    }
  }, [range, refused])

  const choose = (next: OverviewRange) => {
    setEarlier([])
    setRange(next)
  }
  const zoom = (from: string, to: string) => {
    setEarlier((stack) => [...stack, range])
    setRange(zoomedTo(from, to))
  }
  const back = () => {
    const previous = earlier[earlier.length - 1]
    if (!previous) return
    setEarlier((stack) => stack.slice(0, -1))
    setRange(previous)
  }

  if (error) return <EmptyState title="The service is not answering" description={error} />
  if (!data) return <LoadingState label="Reading the overview" />

  const gateOpen = data.unstamped_starts > 0
  // Every card that is about a period answers for the range on screen, from the
  // same answer the charts are drawn from. The p95 and orphan cards used to
  // answer for the last hour beside charts answering for a week, which is two
  // periods on one page with nothing saying so. The live hour is only a
  // fallback until the range has answered.
  const over = series ? rangeLabel(range) : 'the last hour'
  // Absent and null are different answers. Null is a range in which nothing
  // was measured. Absent is a service older than this console, mid deploy,
  // which does not send the figure at all: then the card keeps the live hour
  // and says so, rather than claiming nothing was measured.
  const overall = series?.latency.overall
  const p95 = overall === undefined ? data.latency_p95_ms : overall.p95
  const p95Over = overall === undefined ? 'the last hour' : over
  const budgetUsed = p95 === null ? 0 : Math.round((p95 / OCPP_WINDOW_MS) * 100)

  // Over the range on screen, from the series, so the card and the line on the
  // chart below are the same number. Falling back to the live hour only while
  // the range has not answered yet.
  const overRange = rangeRefusal(series)
  const refusal = overRange
    ? refusalReading(overRange.rate, overRange.total)
    : refusalReading(data.refusal_rate, data.decisions_per_hour)
  const abandoned = data.settlements_abandoned ?? 0
  const orphansRecently = series
    ? series.orphans.total
    : (data.orphan_session_events_recent ?? data.orphan_session_events)


  // ALERTS THAT REACH NOBODY. Named by the service, never derived here.
  //
  // A trigger that did not exist before a release has no rule in any
  // deployment, so "no recipient" is its normal state on the day it ships:
  // the sender works, the subject exists, and the mail goes to an empty list.
  // It does not fail, it reassures, which is why it belongs on the page
  // somebody already has open rather than only on Settings, where you go
  // having already decided to look.
  const silent = data.alerts_reaching_nobody ?? []

  return (
    <div className={styles.page}>
      {silent.length > 0 ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>
              {silent.length === 1
                ? 'An alert reaches nobody'
                : `${silent.length} alerts reach nobody`}
            </MessageBarTitle>
            {silent.join(', ')}{' '}
            {silent.length === 1 ? 'is raised by' : 'are raised by'} this service and{' '}
            {silent.length === 1 ? 'has' : 'have'} no enabled rule with a recipient, so
            nothing is sent when {silent.length === 1 ? 'it fires' : 'they fire'}. These
            are the ones that say an authority approved and this service refused, or that
            a session will never be billed. Set the recipients in Settings and press Send
            a test.
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {gateOpen ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>The go-live gate is open</MessageBarTitle>
            {data.unstamped_starts} session {data.unstamped_starts === 1 ? 'start' : 'starts'} found
            no stamp at an eligible charge point. Those charge points authorized locally, so the
            sessions reach no authority and will not be billed.
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {/* The period's figures first (DEC-28 C2), the health below them. */}
      <OverviewDashboard />
      <SectionLabel>Health, and the charts over time</SectionLabel>

      <RangePicker
        range={range}
        onChange={choose}
        refused={refused}
        onBack={earlier.length > 0 ? back : undefined}
      />

      <div className={styles.grid}>
        <KpiCard
          title="Unstamped starts"
          value={data.unstamped_starts}
          tone={gateOpen ? 'danger' : 'success'}
          caption={
            gateOpen ? 'Real energy that will not be billed' : 'Zero is the only good value'
          }
        />
        {/* A token that could not be encrypted is stored as nothing, and the
            authorization went on. Shown only when the service reports it, so
            a console ahead of its service shows no number it was not given. */}
        {data.tokens_not_kept !== undefined ? (
          <KpiCard
            title="Tokens not kept"
            value={data.tokens_not_kept}
            tone={data.tokens_not_kept > 0 ? 'danger' : 'success'}
            caption="Encryption failed, the authorization went on. Zero is the only good value"
          />
        ) : null}
        {/* The window, with the total beside it in words.
            Not an error. Sessions that started before this service took the
            registration have no stamp and produce these legitimately, so the
            count is expected to fall to zero rather than to start there.
            And the total only grows, because nothing deletes an orphan row, so
            on its own it can never show that a repair upstream worked. The
            number here is the last hour; the total is context, not the
            reading. */}
        <KpiCard
          title="Orphan session events"
          value={orphansRecently}
          tone={orphansRecently > 0 ? 'warning' : 'success'}
          caption={orphanCaption(orphansRecently, data.orphan_session_events, over)}
        />
        {/* Always, unlike the verdict count below it, and the difference is
            what the number is about. An undelivered verdict is a projection of
            a record we keep. An abandoned settlement is a session that will
            not be billed until a person finishes it, so a zero is an answer
            somebody wants to see rather than noise. */}
        <KpiCard
          title="Settlements abandoned"
          value={abandoned}
          tone={abandoned > 0 ? 'warning' : 'success'}
          caption={
            abandoned > 0
              ? 'Refused in a way resending cannot fix. Each needs a person'
              : 'Every settlement was delivered or is still queued'
          }
        />
        {/* A THIRD STATE, and the label is what makes it worth its own card.
            These were never formed: not queued, not sent, nothing to retry,
            and the stop does not come again. Counted over the last day rather
            than over all history, because a count that cannot show a spike
            cannot answer the question this card is for.

            Always, like the one above it and for the same reason: a zero says
            every session that ended can be billed, which is an answer
            somebody wants to see. */}
        <KpiCard
          title="Settlements never formed, last day"
          value={incompleteTotal(data.settlements_incomplete)}
          tone={incompleteTotal(data.settlements_incomplete) > 0 ? 'warning' : 'success'}
          caption={incompleteCaption(data.settlements_incomplete)}
        />
        {/* Kept here and sent to no service (DEC-25). Not a fault: a refused
            card's session is meant to stay here. */}
        {data.unowned_events && unownedShown(data.unowned_events) ? (
          <KpiCard
            title="Unowned events, last day"
            value={unownedTotal(data.unowned_events.last_day)}
            caption={unownedCaption(data.unowned_events)}
          />
        ) : null}
        {/* ITS OWN CARD, AND NOT ADDED TO THE ONE ABOVE. A bridge forward
            nobody retries is a session Hubject is holding open, or one they
            were never told ended. Both need a person and it is not the same
            person, so adding it to "Settlements abandoned" would send
            somebody to complete a settlement that does not exist.

            Only when there are any, for the reason the verdict card gives
            below: the Hubject lane is switched off, so this is a permanent
            zero until somebody turns it on. */}
        {(data.bridge_forwards_abandoned ?? 0) > 0 ? (
          <KpiCard
            title="Bridge forwards abandoned"
            value={data.bridge_forwards_abandoned ?? 0}
            tone="warning"
            caption="Hubject refused finally. Each may be a session still open there"
          />
        ) : null}
        {/* Only when there are any. A permanent zero beside two other zeros
            is three numbers an eye stops reading, and this one is not a gate:
            the decisions are recorded here regardless. */}
        {data.undelivered_verdicts > 0 ? (
          <KpiCard
            title="Undelivered verdicts"
            value={data.undelivered_verdicts}
            tone="warning"
            caption="Formed for the Hub and not accepted"
          />
        ) : null}
        {/* The range the reader picked, not a fixed hour. Both of these are
            counted over the same window as every graph below, from the same
            answer, so a reader comparing a card with a chart is comparing the
            same period. */}
        <KpiCard
          title="Decisions"
          value={series ? series.decisions.total : data.decisions_per_hour}
          caption={`In ${over}`}
        />
        {/* The caption used to say "in the same window" over a statistic that
            had no window at all. Now it names the count it is a rate of, so a
            reader can see what the percentage is over rather than trusting
            that two cards mean the same thing. */}
        <KpiCard
          title="Refusal rate"
          value={refusal.value}
          unit={refusal.unit}
          caption={refusal.caption}
        />
        <KpiCard
          title="p95 end to end"
          value={p95 === null ? 'none' : p95}
          unit={p95 === null ? undefined : 'ms'}
          caption={p95 === null ? `Nothing was measured in ${p95Over}` : `In ${p95Over}`}
          tone={budgetUsed > 80 ? 'warning' : 'neutral'}
          footer={
            <MetricPill
              label="Of the OCPP window"
              value={`${budgetUsed}%`}
              tone={budgetUsed > 80 ? 'warning' : 'neutral'}
            />
          }
        />
      </div>

      <OverviewGraphs series={series} error={seriesError} loading={refetching} onZoom={zoom} />

      <div>
        <SectionLabel>Authorities</SectionLabel>
        <div className={styles.authorities}>
          {data.authorities.map((authority) => (
            <AuthorityTile key={authority.authority} authority={authority} />
          ))}
        </div>
        <Caption1 className={styles.note}>
          Each of these is that authority's own answer to its health check, not an assumption.
          A degraded authority keeps receiving its traffic: sending it elsewhere would settle a
          session against a system that never authorized it (ADR-001). Unknown means we asked
          and could not tell.
        </Caption1>
      </div>

      <SectionCard
        title="Channel stamps"
        description="The short-lived binding of charge point and identifier to the authority that answered. A session that starts on no stamp is the number above."
      >
        <div className={styles.pills}>
          <MetricPill label="Pending" value={data.stamps.pending} />
          <MetricPill label="Bound" value={data.stamps.bound} tone="success" />
          <MetricPill
            label="Expired"
            value={data.stamps.expired}
            tone={data.stamps.expired > 0 ? 'warning' : 'neutral'}
          />
        </div>
      </SectionCard>

      {/* WHAT DECIDES MEMBERSHIP, RATHER THAN WHAT WE MEANT TO BUILD.
          This card said "Location tree", described a cache refreshed on
          `canonical.location_tree.changed`, and drew a green "Resolving" badge
          over three literals in the route. There is no tree: membership is a
          prefix test on the charge point id. An operator was being told a
          component was healthy that does not exist, and this is the page they
          would check first when a station stops routing to TIM (FIND-45). */}
      <SectionCard
        title="Estate membership"
        description="Which charge points the estate rule admits, and how it decides."
      >
        <div className={styles.pills}>
          <MetricPill label="Decided by" value={data.estate.decided_by} />
          {/* The prefix is the whole rule, so it is on the page rather than in
              an environment somebody has to have access to read. Somebody
              asking why a station did not route to TIM compares this against
              the id in front of them. */}
          <MetricPill label="Charge point id starts with" value={data.estate.prefix} />
          <MetricPill label="Estate" value={data.estate.name} />
          <MetricPill
            label="Station list"
            value={
              data.estate.catalogue?.configured
                ? `${data.estate.catalogue.stations} E4E charge points`
                : 'not configured yet'
            }
          />
        </div>
        {data.estate.catalogue?.configured ? (
          <CatalogueShadow catalogue={data.estate.catalogue} />
        ) : null}
        {data.estate.tree_note ? (
          <Caption1 className={styles.estateNote}>{data.estate.tree_note}</Caption1>
        ) : null}
      </SectionCard>
    </div>
  )
}

/**
 * What the station list would have decided, which today decides nothing.
 *
 * THE TWO DIRECTIONS ARE NOT ONE NUMBER AND ARE NOT SHOWN AS ONE. The charge
 * points the list would START sending to TIM are money against a BMW cost
 * centre, so they are named one by one: a count says "three" and tells nobody
 * which three, and nobody can check three unnamed stations. The ones it would
 * STOP sending are a refusal somebody reports the same day, so a count is
 * enough (FIND-45, DEC-22 step 2).
 */
function CatalogueShadow({
  catalogue,
}: {
  catalogue: NonNullable<OverviewData['estate']['catalogue']>
}) {
  const styles = useStyles()
  const shadow = catalogue.shadow
  return (
    <div className={styles.shadow}>
      <Caption1 className={styles.estateNote}>
        {catalogue.pulled_at
          ? `Last read ${formatTimestamp(catalogue.pulled_at)}: ` +
            `${catalogue.stations} E4E charge points and ${catalogue.connectors} connectors` +
            // The Hub offers every station it holds and this service keeps only
            // the E4E ones, so showing both numbers makes the filter visible
            // rather than leaving a reader to wonder why the count is small.
            (catalogue.offered
              ? `, kept from the ${catalogue.offered} the Hub offered.`
              : '.')
          : 'Never read yet. The list decides nothing until it has been.'}
        {catalogue.last_error ? ` Last attempt failed: ${catalogue.last_error}` : ''}
      </Caption1>
      {catalogue.pulled_at ? (
        <Caption1 className={styles.estateNote}>
          {/* WHICH MARK, because it is changing. The Hub is adding an explicit
              estate field and the stations will move onto it one group at a
              time, so a sentence naming the source would go stale on the day
              the data changed and nobody would be looking. */}
          {`Counted as E4E by ${catalogue.marks?.length ? catalogue.marks.join(' and ') : 'no recorded mark'}. `}
          {/* AND WHAT IT CANNOT ANSWER. A current type is a fuel grade, so a
              panel that shows only the size of the list invites the reader to
              assume the rest is known. */}
          {catalogue.connectors_unknown
            ? `${catalogue.connectors_unknown} of ${catalogue.connectors} connectors cannot say AC or DC. `
            : 'Every connector in the list can say AC or DC. '}
          {/* Counts that are commonly 1 on a small estate and 41 on the real
              one, so the sentence has to read at both. "1 charge points have"
              is what it said in the preview. */}
          {catalogue.stations_without_connectors
            ? `${catalogue.stations_without_connectors} charge ${catalogue.stations_without_connectors === 1 ? 'point has' : 'points have'} no connector at all, and ${catalogue.stations_without_connectors === 1 ? 'is' : 'are'} unknown for every connector. `
            : ''}
          {catalogue.stations_mixed
            ? `${catalogue.stations_mixed} ${catalogue.stations_mixed === 1 ? 'carries' : 'carry'} both AC and DC, so an authorization that names no connector is unknown rather than a guess.`
            : ''}
        </Caption1>
      ) : null}
      <Caption1 className={styles.estateNote}>
        Compared against the prefix on {shadow.compared.toLocaleString('en-GB')} decisions in
        the last seven days. The list routes nothing: it is recorded beside what the prefix
        decided.
      </Caption1>
      {shadow.would_route_to_tim.length ? (
        <div className={styles.pills}>
          {shadow.would_route_to_tim.slice(0, 12).map((station) => (
            <MetricPill
              key={station.station_id}
              label={
                // A STATION KNOWN ONLY BY A NAME IT NO LONGER USES is a weaker
                // claim than one matching the serial it answers to today, and
                // this is the list somebody reads before acting.
                station.through_retired_name
                  ? station.through_retired_name === station.decisions
                    ? `would go to TIM, matched only the retired ${station.matched_namespace ?? 'name'}`
                    : `would go to TIM, ${station.through_retired_name} of ${station.decisions} through a retired name`
                  : `would go to TIM, matched ${station.matched_namespace ?? 'a name'}`
              }
              value={`${station.station_id}, ${station.bmw_cards} BMW card${station.bmw_cards === 1 ? '' : 's'}`}
            />
          ))}
        </div>
      ) : (
        <Caption1 className={styles.estateNote}>
          No charge point would start going to TIM. That is the direction that moves money,
          so it is named one by one when there is anything to name.
        </Caption1>
      )}
      <div className={styles.pills}>
        <MetricPill
          label="would stop going to TIM"
          value={`${shadow.would_leave_tim} charge points`}
        />
        <MetricPill
          label="names claimed by two stations"
          value={`${shadow.ambiguous_names} decisions`}
        />
      </div>
    </div>
  )
}

function AuthorityTile({ authority }: { authority: AuthorityHealth }) {
  const styles = useStyles()
  const now = useObservationClock()
  const shown = laneAppearance({ status: authority.status, detail: authority.detail })
  const age = observationAge(authority.observed_at, now)
  return (
    <SectionCard
      title={authority.authority}      // One observation, the same rule the settings card uses. Unknown is a
      // warning and not neutral: not knowing whether the authorization path
      // works is a question, and a question about this deserves attention.
      action={<StatusBadge status={shown.tone} label={shown.status} />}
    >
      <div className={styles.pills}>
        <MetricPill
          label="Route budget"
          value={`${authority.timeout_ms} ms`}
          tone={authority.status === 'ok' ? 'neutral' : 'warning'}
        />
        {/* Ours. When this service last looked at the lane, on every lane and
            on every outcome including the ones where nothing answered. It was
            missing, so a red card carried no age and the only way to ask "is
            this still true" was to press Test, which made the red look
            invented. */}
        {age ? (
          <MetricPill
            label="Checked"
            value={age.label}
            tone={age.stale ? 'warning' : 'neutral'}
          />
        ) : null}
        {/* Theirs, and a different fact: when that authority last checked its
            own upstream, which it tells us in its answer. This used to be the
            chip labelled "Checked", where it read as our observation. */}
        {authority.upstream_checked_at ? (
          <MetricPill
            label="Their upstream"
            value={formatTimestamp(authority.upstream_checked_at)}
          />
        ) : null}
      </div>
      {authority.credential === 'missing' ? (
        // Said out loud next to a green health, because that is the exact
        // shape of the trap: the lane answers, and cannot carry a session.
        <Caption1 className={styles.note}>
          No credential is configured for this lane, so every forward is refused.
        </Caption1>
      ) : null}
      {shown.detail ? (
        // The reason, in the words of whatever answered. Without it "down" is
        // a colour, and the operator still has to go and find out why.
        <Caption1 className={styles.note}>{shown.detail}</Caption1>
      ) : null}
    </SectionCard>
  )
}
